import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Users, Building2, Mail, Shield, UserPlus, Trash2,
  ArrowLeft, Copy, Check, X, ChevronDown, Briefcase, BarChart3, TrendingUp, Trophy,
  Link2, Loader2, AlertCircle, ArrowRightLeft, Send
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RoleBadge({ role }) {
  const styles = {
    admin: 'bg-violet-100 text-violet-700 border-violet-200',
    manager: 'bg-amber-100 text-amber-700 border-amber-200',
    sales: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[role] || styles.sales}`}>
      {role === 'admin' ? 'Admin' : role === 'manager' ? 'Manager' : 'Sales Rep'}
    </span>
  );
}

function MembersTab({ members, departments, org, onRefresh, user }) {
  const [changingRole, setChangingRole] = useState(null);
  const [changingDept, setChangingDept] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);  // {source_member}
  const [transferTo, setTransferTo] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);  // Replace-this-rep wizard
  const onOpenTransfer = (member) => { setTransferTarget(member); setTransferTo(''); };
  const onOpenReplace = (member) => { setReplaceTarget(member); };
  const transferCalls = async () => {
    if (!transferTarget || !transferTo) return;
    setTransferring(true);
    try {
      const { data } = await axios.post(`${API}/org/members/${transferTarget._id}/transfer-calls-to/${transferTo}`);
      alert(data.message);
      setTransferTarget(null);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/org/members/${memberId}/role`, { role: newRole });
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update role');
    }
    setChangingRole(null);
  };

  const handleDeptChange = async (memberId, deptId) => {
    try {
      await axios.put(`${API}/org/members/${memberId}/department`, { department_id: deptId || null });
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update department');
    }
    setChangingDept(null);
  };

  const handleRemove = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the organization?`)) return;
    try {
      await axios.delete(`${API}/org/members/${memberId}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove member');
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-3">
      {members.map((member) => (
        <div
          key={member.email}
          className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-lg hover:border-zinc-300 transition-colors"
          data-testid={`member-row-${member.email}`}
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 font-semibold text-sm flex-shrink-0">
              {member.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-zinc-900 truncate">{member.name}</div>
              <div className="text-sm text-zinc-500 truncate">{member.email}</div>
              {member.department_name && (
                <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {member.department_name}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {changingRole === member._id ? (
              <Select onValueChange={(v) => handleRoleChange(member._id, v)}>
                <SelectTrigger className="w-32 h-8 text-xs">
                  <SelectValue placeholder={member.role} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="sales">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <button onClick={() => isAdmin && setChangingRole(member._id)} className={isAdmin ? 'cursor-pointer' : ''}>
                <RoleBadge role={member.role} />
              </button>
            )}

            {isAdmin && changingDept === member._id ? (
              <Select onValueChange={(v) => handleDeptChange(member._id, v === '__none__' ? null : v)}>
                <SelectTrigger className="w-36 h-8 text-xs">
                  <SelectValue placeholder="Select dept..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Department</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : isAdmin ? (
              <Button variant="ghost" size="sm" className="text-xs text-zinc-500" onClick={() => setChangingDept(member._id)}>
                <Building2 className="w-3 h-3 mr-1" />
                {member.department_name || 'Assign'}
              </Button>
            ) : null}

            {isAdmin && member._id !== org?.owner_id && member._id !== user?.id && (
              <>
                <Button
                  variant="ghost" size="icon"
                  className="text-zinc-400 hover:text-emerald-600 h-8 w-8"
                  onClick={() => onOpenTransfer(member)}
                  title="Transfer this rep's calls to another rep before removing them"
                  data-testid={`transfer-member-${member.email}`}
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="text-zinc-400 hover:text-violet-700 h-8 w-8"
                  onClick={() => onOpenReplace(member)}
                  title="Replace this rep with a new hire (guided wizard)"
                  data-testid={`replace-member-${member.email}`}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="text-zinc-400 hover:text-red-600 h-8 w-8"
                  onClick={() => handleRemove(member._id, member.name)}
                  data-testid={`remove-member-${member.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
      {members.length === 0 && (
        <div className="text-center py-12 text-zinc-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No team members yet</p>
        </div>
      )}

      {/* Transfer-calls modal */}
      {transferTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setTransferTarget(null)} data-testid="transfer-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Transfer calls from {transferTarget.name}</h3>
            <p className="text-sm text-zinc-600 mb-4">
              Pick the rep who should inherit all of <span className="font-semibold">{transferTarget.name}</span>&apos;s historical sessions,
              summaries, and meeting bots. After transfer you can safely remove this rep to free the seat.
            </p>
            <label className="text-xs font-medium text-zinc-700 mb-1.5 block">Transfer to</label>
            <Select value={transferTo} onValueChange={setTransferTo}>
              <SelectTrigger className="rounded-md" data-testid="transfer-target-select"><SelectValue placeholder="Choose a rep..." /></SelectTrigger>
              <SelectContent>
                {members.filter(m => m._id !== transferTarget._id).map(m => (
                  <SelectItem key={m._id} value={m._id}>{m.name} · {m.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setTransferTarget(null)} className="rounded-md" data-testid="transfer-cancel">Cancel</Button>
              <Button size="sm" onClick={transferCalls} disabled={transferring || !transferTo} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md" data-testid="transfer-confirm">
                {transferring && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Transfer calls
              </Button>
            </div>
          </div>
        </div>
      )}

      {replaceTarget && (
        <ReplaceRepWizard
          source={replaceTarget}
          members={members}
          departments={departments}
          onClose={() => setReplaceTarget(null)}
          onComplete={() => { setReplaceTarget(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function DepartmentsTab({ departments, members, onRefresh, user }) {
  const [newDept, setNewDept] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');

  const isAdmin = user?.role === 'admin';

  const handleCreate = async () => {
    if (!newDept.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/org/departments`, { name: newDept.trim() });
      setNewDept('');
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create department');
    }
    setCreating(false);
  };

  const handleUpdate = async (deptId) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/org/departments/${deptId}`, { name: editName.trim() });
      setEditing(null);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update department');
    }
  };

  const handleDelete = async (deptId, deptName) => {
    if (!window.confirm(`Delete "${deptName}"? Members will be unassigned.`)) return;
    try {
      await axios.delete(`${API}/org/departments/${deptId}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete department');
    }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex gap-2">
          <Input
            placeholder="New department name..."
            value={newDept}
            onChange={(e) => setNewDept(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="max-w-xs"
            data-testid="new-dept-input"
          />
          <Button onClick={handleCreate} disabled={creating || !newDept.trim()} data-testid="create-dept-btn">
            <Building2 className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {departments.map((dept) => (
          <Card key={dept.id} className="border-zinc-200" data-testid={`dept-card-${dept.id}`}>
            <CardContent className="p-4">
              {editing === dept.id ? (
                <div className="flex gap-2 items-center">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8 text-sm"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(dept.id)}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleUpdate(dept.id)}>
                    <Check className="w-4 h-4 text-green-600" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}>
                    <X className="w-4 h-4 text-zinc-400" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-zinc-900">{dept.name}</h3>
                    <p className="text-xs text-zinc-500 mt-1">
                      {dept.member_count} member{dept.member_count !== 1 ? 's' : ''}
                      {dept.manager_name && ` · Manager: ${dept.manager_name}`}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="sm" className="text-xs"
                        onClick={() => { setEditing(dept.id); setEditName(dept.name); }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-600"
                        onClick={() => handleDelete(dept.id, dept.name)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {departments.length === 0 && (
        <div className="text-center py-12 text-zinc-400">
          <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No departments created yet</p>
        </div>
      )}
    </div>
  );
}

function InvitationsTab({ invitations, departments, org, onRefresh, user }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('sales');
  const [deptId, setDeptId] = useState('');
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const handleResend = async (invId) => {
    setResendingId(invId);
    try {
      const { data } = await axios.post(`${API}/org/invitations/${invId}/resend`);
      if (data.email_sent) toast.success(data.message);
      else toast.warning(data.message, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const baseUrl = window.location.origin;

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    setInviteResult(null);
    try {
      const { data } = await axios.post(`${API}/org/invite`, {
        email: email.trim().toLowerCase(),
        role,
        department_id: deptId || null
      });
      setInviteResult(data);
      setEmail('');
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send invitation');
    }
    setSending(false);
  };

  const handleCancel = async (invId) => {
    try {
      await axios.delete(`${API}/org/invitations/${invId}`);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${baseUrl}/register?invite=${token}`);
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Invite a Team Member</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="admin">Admin</SelectItem>}
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="sales">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Department</Label>
              <Select value={deptId || '__none__'} onValueChange={(v) => setDeptId(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Department</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleInvite} disabled={sending || !email.trim()} data-testid="send-invite-btn">
            <UserPlus className="w-4 h-4 mr-1" />
            {sending ? 'Sending...' : 'Send Invitation'}
          </Button>

          {inviteResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="invite-success">
              <p className="text-sm text-emerald-700">Invitation created! Share this link:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border rounded px-2 py-1 flex-1 truncate">
                  {baseUrl}{inviteResult.invite_link}
                </code>
                <Button
                  size="icon" variant="outline" className="h-8 w-8 flex-shrink-0"
                  onClick={() => copyLink(inviteResult.token)}
                >
                  {copiedId === inviteResult.token ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-zinc-700">Pending Invitations</h3>
        {invitations.map((inv) => (
          <div
            key={inv.id}
            className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-lg"
            data-testid={`invitation-row-${inv.email}`}
          >
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-zinc-400" />
              <div>
                <span className="text-sm font-medium text-zinc-700">{inv.email}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <RoleBadge role={inv.role} />
                  <span className="text-xs text-zinc-400">
                    Sent {new Date(inv.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline" className="h-8 rounded-md text-xs"
                disabled={resendingId === inv.id}
                onClick={() => handleResend(inv.id)}
                title="Re-send the invitation email"
                data-testid={`resend-invitation-${inv.email}`}
              >
                {resendingId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1.5" /> Resend</>}
              </Button>
              <Button
                size="icon" variant="ghost" className="h-8 w-8"
                onClick={() => copyLink(inv.token)}
                title="Copy invite link"
                data-testid={`copy-invitation-link-${inv.email}`}
              >
                {copiedId === inv.token ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
              </Button>
              <Button
                size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-red-600"
                onClick={() => handleCancel(inv.id)}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {invitations.length === 0 && (
          <p className="text-sm text-zinc-400 py-4 text-center">No pending invitations</p>
        )}
      </div>
    </div>
  );
}

function PerformanceTab({ user }) {
  const [analytics, setAnalytics] = useState(null);
  const [deptData, setDeptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isManager = user?.role === 'manager';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        const promises = [];
        if (isAdmin) {
          promises.push(axios.get(`${API}/analytics/overview?show_all=true&days=30`));
          promises.push(Promise.resolve({ data: null }));
        } else if (isManager) {
          promises.push(axios.get(`${API}/analytics/overview?days=30`));
          promises.push(axios.get(`${API}/analytics/department?days=30`));
        }
        const results = await Promise.all(promises);
        setAnalytics(results[0].data);
        setDeptData(results[1]?.data || null);
      } catch (e) {
        console.error('Failed to load analytics:', e);
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [isAdmin, isManager]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!analytics) return <p className="text-sm text-zinc-400 text-center py-8">Failed to load analytics</p>;

  const reps = analytics.rep_performance || [];

  return (
    <div className="space-y-6" data-testid="performance-tab">
      {/* Manager Department Header */}
      {isManager && deptData?.department && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="department-header">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">{deptData.department.name}</h3>
                <p className="text-xs text-zinc-500">Your department · {deptData.total_members} member{deptData.total_members !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isManager && !deptData?.department && (
        <div className="bg-zinc-100 border border-zinc-200 rounded-lg p-4 text-center" data-testid="no-department-notice">
          <p className="text-sm text-zinc-500">You haven&apos;t been assigned to a department yet. Contact your admin.</p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Total Calls (30d)</div>
            <div className="text-2xl font-bold text-zinc-900">
              {isManager && deptData ? deptData.total_calls : analytics.total_calls}
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Completed</div>
            <div className="text-2xl font-bold text-emerald-600">
              {isManager && deptData ? deptData.completed_calls : analytics.completed_calls}
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Avg Score</div>
            <div className="text-2xl font-bold text-brand-primary">
              {isManager && deptData ? deptData.avg_score : analytics.avg_qualification_score}%
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 mb-1">Active Reps</div>
            <div className="text-2xl font-bold text-zinc-900">
              {isManager && deptData ? deptData.total_members : reps.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Member Leaderboard (Manager view) */}
      {isManager && deptData?.members?.length > 0 && (
        <Card className="border-zinc-200" data-testid="dept-leaderboard">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Department Leaderboard (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {deptData.members.map((member, i) => (
                <div
                  key={member.user_id}
                  className={`flex items-center gap-4 p-3 rounded-lg ${i === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-zinc-50'}`}
                  data-testid={`dept-leaderboard-row-${i}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-zinc-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-zinc-200 text-zinc-600'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-medium text-sm text-zinc-900 truncate">{member.name}</span>
                        <span className="text-xs text-zinc-400 ml-2">{member.email}</span>
                      </div>
                      <span className="text-sm font-semibold text-zinc-700">{member.avg_score}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-zinc-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            member.avg_score >= 70 ? 'bg-emerald-500' : member.avg_score >= 40 ? 'bg-amber-500' : 'bg-red-400'
                          }`}
                          style={{ width: `${Math.min(member.avg_score, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-zinc-400 flex-shrink-0">
                        {member.total_calls} call{member.total_calls !== 1 ? 's' : ''} · {member.completed_calls} done
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Leaderboard */}
      {isAdmin && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Rep Leaderboard (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reps.length > 0 ? (
              <div className="space-y-2">
                {reps.map((rep, i) => (
                  <div
                    key={rep.user_id}
                    className={`flex items-center gap-4 p-3 rounded-lg ${i === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-zinc-50'}`}
                    data-testid={`leaderboard-row-${i}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-zinc-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-zinc-200 text-zinc-600'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-zinc-900 truncate">{rep.name}</span>
                        <span className="text-sm font-semibold text-zinc-700">{rep.avg_score}%</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-zinc-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              rep.avg_score >= 70 ? 'bg-emerald-500' : rep.avg_score >= 40 ? 'bg-amber-500' : 'bg-red-400'
                            }`}
                            style={{ width: `${Math.min(rep.avg_score, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-zinc-400 flex-shrink-0">{rep.total_calls} call{rep.total_calls !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 text-center py-6">No call data yet</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Averages */}
      {Object.keys(analytics.category_averages || {}).length > 0 && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-brand-primary" />
              Category Performance ({isManager && deptData ? 'Department' : 'Team'} Average)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.entries(analytics.category_averages).map(([cat, score]) => (
                <div key={cat} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                  <span className="text-sm font-medium text-zinc-700">{cat}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-zinc-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${
                          score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-400'
                        }`}
                        style={{ width: `${Math.min(score, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-semibold ${
                      score >= 70 ? 'text-emerald-600' : score >= 40 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {score}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Framework Usage */}
      {Object.keys(analytics.framework_usage || {}).length > 0 && (
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Framework Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              {Object.entries(analytics.framework_usage).map(([fw, count]) => (
                <div key={fw} className="flex items-center gap-2 bg-zinc-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium text-zinc-700 uppercase">{fw}</span>
                  <span className="text-xs bg-white px-2 py-0.5 rounded-full text-zinc-500">{count} calls</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const CRM_PROVIDERS = [
  { id: 'hubspot', name: 'HubSpot', placeholder: 'Private App access token', helpUrl: 'https://developers.hubspot.com/docs/api/private-apps', domainLabel: null },
  { id: 'pipedrive', name: 'Pipedrive', placeholder: 'API Token (Settings > Personal Preferences)', helpUrl: 'https://pipedrive.readme.io/docs/core-api-concepts-about-pipedrive-api', domainLabel: 'Company domain (e.g. mycompany)' },
  { id: 'zoho', name: 'Zoho CRM', placeholder: 'OAuth access token', helpUrl: 'https://www.zoho.com/crm/developer/docs/api/v7/', domainLabel: 'API domain (e.g. com, eu, in)' },
];

function CrmTab({ user }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [formData, setFormData] = useState({ provider: '', api_key: '', domain: '' });
  const [error, setError] = useState('');

  const loadConnections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/crm/connections`);
      setConnections(data);
    } catch (e) {
      console.error('Failed to load CRM connections:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const connectedProviders = connections.map(c => c.provider);

  const handleConnect = async (provider) => {
    if (!formData.api_key.trim()) {
      setError('API key is required');
      return;
    }
    setConnecting(provider);
    setError('');
    try {
      await axios.post(`${API}/crm/connect`, {
        provider,
        api_key: formData.api_key,
        domain: formData.domain || undefined,
      });
      setFormData({ provider: '', api_key: '', domain: '' });
      await loadConnections();
    } catch (e) {
      setError(e.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}?`)) return;
    try {
      await axios.post(`${API}/crm/disconnect`, { provider });
      await loadConnections();
    } catch (e) {
      console.error('Disconnect failed:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-tab">
      <div className="text-sm text-zinc-500 mb-4">
        Connect your CRM to push call summaries directly from PipeGuide. Each rep connects their own credentials.
      </div>

      {/* Connected CRMs */}
      {connections.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wider">Connected</h3>
          {connections.map(conn => {
            const prov = CRM_PROVIDERS.find(p => p.id === conn.provider);
            return (
              <Card key={conn.provider} className="border-green-200 bg-green-50/50" data-testid={`crm-connected-${conn.provider}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-zinc-900">{prov?.name || conn.provider}</div>
                      <div className="text-xs text-zinc-500">
                        Connected {conn.connected_at ? new Date(conn.connected_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDisconnect(conn.provider)}
                    data-testid={`crm-disconnect-${conn.provider}`}
                  >
                    Disconnect
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Available CRMs */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wider">
          {connections.length > 0 ? 'Add Another CRM' : 'Available Integrations'}
        </h3>
        {CRM_PROVIDERS.filter(p => !connectedProviders.includes(p.id)).map(prov => (
          <Card key={prov.id} className="border-zinc-200" data-testid={`crm-available-${prov.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div>
                    <div className="font-medium text-zinc-900">{prov.name}</div>
                    <a href={prov.helpUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline">
                      API docs
                    </a>
                  </div>
                </div>
              </div>

              {formData.provider === prov.id ? (
                <div className="space-y-3 pt-2 border-t border-zinc-100">
                  <div>
                    <Label className="text-xs">API Key / Token</Label>
                    <Input
                      type="password"
                      placeholder={prov.placeholder}
                      value={formData.api_key}
                      onChange={e => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                      data-testid={`crm-api-key-${prov.id}`}
                    />
                  </div>
                  {prov.domainLabel && (
                    <div>
                      <Label className="text-xs">{prov.domainLabel}</Label>
                      <Input
                        placeholder={prov.id === 'zoho' ? 'com' : 'mycompany'}
                        value={formData.domain}
                        onChange={e => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                        data-testid={`crm-domain-${prov.id}`}
                      />
                    </div>
                  )}
                  {error && (
                    <div className="flex items-center gap-2 text-sm text-red-600" data-testid="crm-error">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleConnect(prov.id)}
                      disabled={connecting === prov.id}
                      data-testid={`crm-connect-btn-${prov.id}`}
                    >
                      {connecting === prov.id ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Connecting...</>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setFormData({ provider: '', api_key: '', domain: '' }); setError(''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setFormData({ provider: prov.id, api_key: '', domain: '' })}
                  data-testid={`crm-setup-${prov.id}`}
                >
                  Set Up
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function TeamManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [orgRes, membersRes, deptsRes, invRes] = await Promise.all([
        axios.get(`${API}/org`),
        axios.get(`${API}/org/members`),
        axios.get(`${API}/org/departments`),
        user?.role === 'admin' || user?.role === 'manager'
          ? axios.get(`${API}/org/invitations`)
          : Promise.resolve({ data: [] })
      ]);
      setOrg(orgRes.data);
      setMembers(membersRes.data);
      setDepartments(deptsRes.data);
      setInvitations(invRes.data);
    } catch (e) {
      console.error('Failed to load team data:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => { loadData(); }, [loadData]);

  const tabs = [
    { id: 'members', label: 'Members', icon: Users, count: members.length },
    { id: 'departments', label: 'Departments', icon: Building2, count: departments.length },
  ];

  if (user?.role === 'admin' || user?.role === 'manager') {
    tabs.push({ id: 'invitations', label: 'Invitations', icon: Mail, count: invitations.length });
    tabs.push({ id: 'performance', label: 'Performance', icon: BarChart3 });
    tabs.push({ id: 'crm', label: 'CRM', icon: Link2 });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="team-management-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {user?.role === 'admin' ? (
              <Link to="/admin" className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            ) : (
              <Link to="/" className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            <div>
              <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Team Management
              </h1>
              <p className="text-sm text-zinc-500">
                {org?.name} · {members.length} member{members.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role={user?.role} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-zinc-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-zinc-900">{members.length}</div>
                <div className="text-xs text-zinc-500">Team Members</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-zinc-900">{departments.length}</div>
                <div className="text-xs text-zinc-500">Departments</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-accent-muted rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-zinc-900">{invitations.length}</div>
                <div className="text-xs text-zinc-500">Pending Invites</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg w-fit mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-200 text-zinc-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'members' && (
          <MembersTab members={members} departments={departments} org={org} onRefresh={loadData} user={user} />
        )}
        {activeTab === 'departments' && (
          <DepartmentsTab departments={departments} members={members} onRefresh={loadData} user={user} />
        )}
        {activeTab === 'invitations' && (
          <InvitationsTab invitations={invitations} departments={departments} org={org} onRefresh={loadData} user={user} />
        )}
        {activeTab === 'performance' && (
          <PerformanceTab user={user} />
        )}
        {activeTab === 'crm' && (
          <CrmTab user={user} />
        )}
      </main>
    </div>
  );
}

// =========================================================================
// ReplaceRepWizard — 3-step flow: transfer calls → remove old → invite new
// =========================================================================
function ReplaceRepWizard({ source, members, departments, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [absorberId, setAbsorberId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState(source.role || 'sales');
  const [newDept, setNewDept] = useState(source.department_id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const otherMembers = members.filter(m => m._id !== source._id);

  const next = () => setStep(s => Math.min(s + 1, 3));
  const back = () => setStep(s => Math.max(s - 1, 1));

  const execute = async () => {
    setError(''); setBusy(true);
    try {
      // Single atomic call: backend transfers calls → removes source → creates invite
      const { data } = await axios.post(`${API}/org/members/${source._id}/replace`, {
        absorber_id: absorberId,
        new_email: newEmail.trim().toLowerCase(),
        new_role: newRole,
        department_id: newDept || null,
      });
      toast.success(data.message || `Replaced ${source.name} successfully.`);
      onComplete();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Something went wrong during replace. Check the Team page for current state.';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const canNext1 = !!absorberId;
  const canNext2 = newEmail.includes('@') && newName.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="replace-wizard">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-1">Replace {source.name}</h3>
        <p className="text-xs text-zinc-500 mb-4">Step {step} of 3</p>

        {/* Step 1: pick absorber */}
        {step === 1 && (
          <div className="space-y-3" data-testid="rw-step-1">
            <p className="text-sm text-zinc-600">
              Choose the existing rep who should inherit <span className="font-semibold">{source.name}</span>&apos;s
              calls, summaries, and meeting bots.
            </p>
            <Select value={absorberId} onValueChange={setAbsorberId}>
              <SelectTrigger className="rounded-md" data-testid="rw-absorber-select"><SelectValue placeholder="Choose a rep..." /></SelectTrigger>
              <SelectContent>
                {otherMembers.map(m => (
                  <SelectItem key={m._id} value={m._id}>{m.name} · {m.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Step 2: new rep details */}
        {step === 2 && (
          <div className="space-y-3" data-testid="rw-step-2">
            <p className="text-sm text-zinc-600">
              {source.name}&apos;s seat will be freed. Invite their replacement so they can set their own credentials.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Full name</label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Mark Smith" className="rounded-md" data-testid="rw-new-name" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Email</label>
                <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="mark@company.com" className="rounded-md" data-testid="rw-new-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Role</label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="rounded-md" data-testid="rw-new-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales Rep</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {departments && departments.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-700">Department</label>
                  <Select value={newDept} onValueChange={setNewDept}>
                    <SelectTrigger className="rounded-md"><SelectValue placeholder="(optional)" /></SelectTrigger>
                    <SelectContent>
                      {departments.map(d => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: confirm */}
        {step === 3 && (
          <div className="space-y-3" data-testid="rw-step-3">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <div className="font-semibold text-amber-800 mb-1">Confirm the replace</div>
              <ol className="list-decimal pl-5 text-xs text-zinc-700 space-y-1">
                <li>Move all calls from <b>{source.name}</b> → <b>{otherMembers.find(m => m._id === absorberId)?.name}</b></li>
                <li>Remove <b>{source.name}</b> from the team (frees their seat + revokes access)</li>
                <li>Email <b>{newName}</b> at <span className="font-mono">{newEmail}</span> an invitation as <b>{newRole}</b></li>
              </ol>
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
          </div>
        )}

        <div className="flex justify-between gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-md" data-testid="rw-cancel">Cancel</Button>
          <div className="flex gap-2">
            {step > 1 && <Button variant="outline" size="sm" onClick={back} disabled={busy} className="rounded-md" data-testid="rw-back">Back</Button>}
            {step < 3 ? (
              <Button size="sm" onClick={next} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)} className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-md" data-testid="rw-next">Next</Button>
            ) : (
              <Button size="sm" onClick={execute} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white rounded-md" data-testid="rw-execute">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Replace rep
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

